'use client';

import type { ProfileModuleDto } from '@up2stay/up2promo-api-client';
import { ChevronLeft, ChevronRight, ExternalLink, Search } from 'lucide-react';
import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Avatar, AvatarFallback, AvatarImage } from '../../components/avatar';
import { Input } from '../../components/input';
import { cn } from '../../utils/cn';
import { ModuleEntryCard } from './module-entry-card';
import {
  resolvePublicProfileModuleChrome,
  type PublicProfileModuleChrome,
} from '../public-profile-module-copy';

interface TeamMembersModuleDisplayProps {
  module: ProfileModuleDto;
  workspaceUsername?: string | null;
  profileBaseUrl?: string;
  /** Username of the currently displayed public profile; will be excluded from the list. */
  currentUsername?: string;
  /** Optional: resolve relative file paths to full URLs. */
  getFileUrl?: (path: string) => string;
  chrome?: PublicProfileModuleChrome;
}

type TeamMember = {
  id?: string;
  userId?: string;
  username?: string;
  name: string;
  role?: string;
  title?: string;
  avatar?: {
    id: string;
    path: string;
    [key: string]: unknown;
  };
  bio?: string;
  email?: string;
  phone?: string;
  profileUrl?: string;
  order?: number;
  visible?: boolean;
};

type TeamMembersModuleConfig = {
  type: 'team_members';
  members: TeamMember[];
  layout?: 'grid' | 'list' | 'carousel';
  columns?: number;
  showRoles?: boolean;
};

function getTeamMembersConfig(
  rawConfig: Record<string, unknown>,
): TeamMembersModuleConfig {
  return {
    type: 'team_members',
    members: (rawConfig.members as TeamMember[]) || [],
    layout: (rawConfig.layout as 'grid' | 'list' | 'carousel') || 'grid',
    columns: (rawConfig.columns as number) || 3,
    showRoles: rawConfig.showRoles as boolean | undefined,
  };
}

/**
 * Returns the profile URL for a team member when set by the backend (only for public profiles).
 * Relative paths are prefixed with profileBaseUrl.
 */
function getMemberProfileUrl(
  member: TeamMember,
  profileBaseUrl: string,
  workspaceUsername?: string | null,
): string | null {
  const explicit = member.profileUrl?.trim() || null;
  const derived =
    !explicit && workspaceUsername && member.username
      ? `/${workspaceUsername}/${member.username}`
      : null;
  const url = explicit ?? derived;
  if (!url) return null;
  if (url.startsWith('/') && profileBaseUrl) {
    const base = profileBaseUrl.replace(/\/$/, '');
    return base ? `${base}${url}` : url;
  }
  return url;
}

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

const SLIDER_GAP = 16;
const SLIDER_CARD_WIDTH = 160;
const SLIDER_CARD_HEIGHT = 220; /* uniform card height so all match the largest */
const SLIDER_SCROLL_STEP = SLIDER_CARD_WIDTH + SLIDER_GAP;
const SLIDER_ROWS = 2;

/** DEV-16: show search field once a team module has at least this many members. */
const SEARCH_THRESHOLD = 9;

/**
 * Displays a list of team members with avatars, role (job title), and link to their public profile when available.
 * Matches the app preview (ProfileEditPage / ProfilesPage): same card styling, grid slider, borders.
 */
export function TeamMembersModuleDisplay({
  module,
  workspaceUsername,
  profileBaseUrl = '',
  currentUsername,
  getFileUrl = (path) => path,
  chrome,
}: TeamMembersModuleDisplayProps) {
  const copy = chrome ?? resolvePublicProfileModuleChrome(undefined);
  const config = getTeamMembersConfig(module.config || {});

  const normalizedCurrentUsername = (currentUsername ?? '')
    .trim()
    .toLowerCase();

  const visibleMembers = useMemo(
    () =>
      (config.members || [])
        .filter((member) => {
          if (!normalizedCurrentUsername) return true;
          const memberUsername = (member.username ?? '').trim().toLowerCase();
          if (memberUsername && memberUsername === normalizedCurrentUsername) {
            return false;
          }
          const rawUrl = (member.profileUrl ?? '').trim();
          if (
            rawUrl &&
            rawUrl.toLowerCase().endsWith(`/${normalizedCurrentUsername}`)
          ) {
            return false;
          }
          return true;
        })
        .filter((member) => member.visible !== false)
        .map((member, index) => ({ member, index }))
        .sort(
          (a, b) => (a.member.order ?? a.index) - (b.member.order ?? b.index),
        )
        .map(({ member }) => member),
    [config.members, normalizedCurrentUsername],
  );

  const layout = config.layout || 'grid';
  const showRoles = config.showRoles !== false;
  const showSearch = visibleMembers.length >= SEARCH_THRESHOLD;

  // DEV-16: live search across name and role (case-insensitive).
  const [searchQuery, setSearchQuery] = useState('');
  const normalizedQuery = searchQuery.trim().toLowerCase();

  const filteredMembers = useMemo(() => {
    if (!normalizedQuery) return visibleMembers;
    return visibleMembers.filter((m) => {
      const haystack =
        `${m.name} ${m.role ?? ''} ${m.title ?? ''}`.toLowerCase();
      return haystack.includes(normalizedQuery);
    });
  }, [visibleMembers, normalizedQuery]);

  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const left = el.scrollLeft > 2;
    const right = el.scrollLeft < el.scrollWidth - el.clientWidth - 2;
    setCanScrollLeft(left);
    setCanScrollRight(right);
  }, []);

  const scrollSlider = useCallback((direction: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({
      left: direction === 'left' ? -SLIDER_SCROLL_STEP : SLIDER_SCROLL_STEP,
      behavior: 'smooth',
    });
  }, []);

  useEffect(() => {
    if (layout !== 'grid') return;
    updateScrollState();
    const el = scrollRef.current;
    if (!el) return;
    const ro = new ResizeObserver(updateScrollState);
    ro.observe(el);
    return () => ro.disconnect();
  }, [layout, updateScrollState, filteredMembers.length]);

  if (visibleMembers.length === 0) {
    return null;
  }

  const renderMemberCard = (
    member: TeamMember,
    index: number,
    variant: 'grid' | 'gridCompact' | 'list' | 'carousel',
  ) => {
    const profileUrl = getMemberProfileUrl(
      member,
      profileBaseUrl,
      workspaceUsername,
    );
    const roleLabel = member.role || member.title;
    const isList = variant === 'list';

    const content = (
      <>
        <Avatar
          className={isList ? 'h-16 w-16 flex-shrink-0' : 'h-20 w-20 mb-3'}
        >
          {member.avatar?.path ? (
            <AvatarImage
              src={getFileUrl(member.avatar.path)}
              alt={member.name}
              className="object-cover"
            />
          ) : null}
          <AvatarFallback
            className={
              isList
                ? 'bg-primary-solid text-primary-solid-foreground font-bold'
                : 'bg-primary-solid text-primary-solid-foreground text-xl font-bold'
            }
          >
            {getInitials(member.name)}
          </AvatarFallback>
        </Avatar>
        <div
          className={
            isList ? 'flex-1 min-w-0' : 'flex flex-col items-center text-center'
          }
        >
          <div className="font-semibold text-base">{member.name}</div>
          {showRoles && roleLabel && (
            <div className="text-sm text-muted-foreground mt-1">
              {roleLabel}
            </div>
          )}
        </div>
      </>
    );

    const gridCarouselContentClass = 'relative h-full p-5';
    const gridCompactContentClass = 'relative h-full p-4';
    const listContentClass = 'p-5';
    const gridCarouselInnerClass =
      'flex h-full min-h-0 flex-col items-center justify-center text-center';
    const gridCompactInnerClass =
      'flex h-full min-h-0 min-w-0 flex-col items-center justify-center text-center';
    const listInnerClass = 'flex items-start gap-4';

    const cardShell = (
      inner: ReactNode,
      key: string,
      options: {
        className?: string;
        contentClassName: string;
        innerClassName?: string;
        href?: string;
      },
    ) => {
      const body = (
        <>
          {options.href ? (
            <ExternalLink
              aria-hidden="true"
              className="absolute right-2 top-2 size-4 text-muted-foreground/70"
            />
          ) : null}
          {inner}
        </>
      );

      if (options.href) {
        return (
          <ModuleEntryCard
            key={key}
            className={options.className}
            contentClassName={cn('relative', options.contentClassName)}
          >
            <a
              href={options.href}
              target="_blank"
              rel="noopener noreferrer"
              className={cn(
                'block h-full text-foreground no-underline transition-colors',
                options.innerClassName,
              )}
            >
              {body}
            </a>
          </ModuleEntryCard>
        );
      }

      return (
        <ModuleEntryCard
          key={key}
          className={options.className}
          contentClassName={cn(
            options.contentClassName,
            options.innerClassName,
          )}
        >
          {body}
        </ModuleEntryCard>
      );
    };

    const cardHeightClass =
      variant === 'gridCompact' ? 'h-full' : isList ? undefined : 'h-[220px]';
    const cardContentClass = isList
      ? listContentClass
      : variant === 'gridCompact'
        ? gridCompactContentClass
        : gridCarouselContentClass;
    const cardInnerClass = isList
      ? listInnerClass
      : variant === 'gridCompact'
        ? gridCompactInnerClass
        : gridCarouselInnerClass;

    if (profileUrl) {
      return cardShell(content, String(member.id ?? member.userId ?? index), {
        className: cardHeightClass,
        contentClassName: cardContentClass,
        innerClassName: cardInnerClass,
        href: profileUrl,
      });
    }

    return cardShell(content, String(member.id ?? member.userId ?? index), {
      className: cardHeightClass,
      contentClassName: cardContentClass,
      innerClassName: cardInnerClass,
    });
  };

  const gridMinHeight = SLIDER_CARD_HEIGHT * SLIDER_ROWS + SLIDER_GAP;

  const searchField = showSearch ? (
    <div className="relative w-full max-w-xs mb-3">
      <Search
        aria-hidden="true"
        className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
      />
      <Input
        type="search"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        placeholder={copy.teamSearchPlaceholder}
        className="pl-9"
        aria-label={copy.teamSearchAria}
      />
    </div>
  ) : null;

  const emptySearchResult =
    showSearch && filteredMembers.length === 0 ? (
      <div className="text-sm text-muted-foreground py-6 text-center">
        {copy.teamEmpty}
      </div>
    ) : null;

  if (layout === 'grid' && visibleMembers.length <= 4) {
    // Max 2 members per row: 1 → 1 col; 2–4 → 2 cols (e.g. 3 TMs = 2 above, 1 below)
    const cols = visibleMembers.length === 1 ? 1 : 2;
    return (
      <div className="w-full">
        {searchField}
        {emptySearchResult ?? (
          <div
            className="grid w-full gap-4"
            style={{
              gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
              gridAutoRows: '200px',
            }}
          >
            {filteredMembers.map((m, i) => (
              <div key={m.id ?? m.userId ?? i} className="min-w-0 min-h-0">
                {renderMemberCard(m, i, 'gridCompact')}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  if (layout === 'grid' && visibleMembers.length > 4) {
    // DEV-16: render every visible member inside the horizontally scrollable grid.
    return (
      <div className="w-full">
        {searchField}
        {emptySearchResult ?? (
          <div className="relative w-full" style={{ minHeight: gridMinHeight }}>
            <div
              ref={scrollRef}
              onScroll={updateScrollState}
              className="w-full overflow-x-auto overflow-y-hidden pb-2 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
              style={{
                WebkitOverflowScrolling: 'touch',
                minHeight: gridMinHeight,
              }}
            >
              <div
                className="grid gap-4"
                style={{
                  gridTemplateRows: `repeat(${SLIDER_ROWS}, ${SLIDER_CARD_HEIGHT}px)`,
                  gridAutoFlow: 'column',
                  gridAutoColumns: `${SLIDER_CARD_WIDTH}px`,
                  width: 'max-content',
                  minHeight: gridMinHeight,
                }}
              >
                {filteredMembers.map((m, i) => (
                  <div key={m.id ?? m.userId ?? i} className="min-w-0">
                    {renderMemberCard(m, i, 'grid')}
                  </div>
                ))}
              </div>
            </div>
            {(canScrollLeft || canScrollRight) && (
              <>
                <button
                  type="button"
                  onClick={() => scrollSlider('left')}
                  disabled={!canScrollLeft}
                  aria-label={copy.previous}
                  className="absolute left-0 top-1/2 z-10 -translate-y-1/2 flex items-center justify-center size-10 rounded-full bg-background/90 text-foreground shadow-md border border-border/50 hover:bg-background disabled:opacity-30 disabled:pointer-events-none transition-opacity"
                >
                  <ChevronLeft className="size-5" />
                </button>
                <button
                  type="button"
                  onClick={() => scrollSlider('right')}
                  disabled={!canScrollRight}
                  aria-label={copy.next}
                  className="absolute right-0 top-1/2 z-10 -translate-y-1/2 flex items-center justify-center size-10 rounded-full bg-background/90 text-foreground shadow-md border border-border/50 hover:bg-background disabled:opacity-30 disabled:pointer-events-none transition-opacity"
                >
                  <ChevronRight className="size-5" />
                </button>
              </>
            )}
          </div>
        )}
      </div>
    );
  }

  if (layout === 'list') {
    return (
      <div className="w-full">
        {searchField}
        {emptySearchResult ?? (
          <div className="w-full space-y-3">
            {filteredMembers.map((m, i) => renderMemberCard(m, i, 'list'))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="w-full">
      {searchField}
      {emptySearchResult ?? (
        <div className="w-full overflow-x-auto">
          <div className="flex gap-4 pb-2">
            {filteredMembers.map((m, i) => (
              <div key={m.id ?? m.userId ?? i} className="flex-shrink-0 w-48">
                {renderMemberCard(m, i, 'carousel')}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
